(() => {
  const MOBILE_QUERY = '(max-width: 749px)';
  const SEARCH_SELECTOR = '.rps-csb-container';
  const SLOT_SELECTOR = '[data-mobile-rapid-search-slot]';
  const DUPLICATE_CLASS = 'rps-mobile-duplicate-hidden';
  const EMPTY_HOST_CLASS = 'rps-mobile-empty-host';
  const SEARCH_PLACEHOLDER_FULL = 'Search OEM, SKU or part number';
  const SEARCH_PLACEHOLDER_COMPACT = 'Search OEM, SKU or part';
  const SEARCH_PLACEHOLDER_NARROW = 'Search OEM or SKU';

  const mediaQuery = window.matchMedia(MOBILE_QUERY);
  let activeSearch = null;
  let originalParent = null;
  let originalNextSibling = null;
  let resizeObserver = null;
  let scheduled = false;
  let resizeScheduled = false;

  const getSlot = () => document.querySelector(SLOT_SELECTOR);
  const getStickyHeader = () => document.querySelector('sticky-header');

  const refreshHeaderHeight = () => {
    requestAnimationFrame(() => {
      const stickyHeader = getStickyHeader();
      const sectionHeader = document.querySelector('.section-header');

      if (stickyHeader && typeof stickyHeader.setHeaderHeight === 'function') {
        stickyHeader.setHeaderHeight();
      } else if (sectionHeader) {
        document.documentElement.style.setProperty('--header-height', `${sectionHeader.offsetHeight}px`);
      }
    });
  };

  const normalizeMobileHeaderRow = () => {
    const header = document.querySelector('.section-header .header');
    if (!header) return;

    if (!mediaQuery.matches) {
      header.style.removeProperty('height');
      header.style.removeProperty('min-height');
      header.style.removeProperty('max-height');
      return;
    }

    requestAnimationFrame(() => {
      const controls = [
        header.querySelector('header-drawer'),
        header.querySelector('.header__heading-link'),
        header.querySelector('.header__icons'),
      ].filter(Boolean);

      const visibleControls = controls.filter((element) => {
        const rect = element.getBoundingClientRect();
        const style = window.getComputedStyle(element);
        return style.display !== 'none' && rect.width > 0 && rect.height > 0;
      });

      if (!visibleControls.length) return;

      const headerRect = header.getBoundingClientRect();
      const contentBottom = Math.max(
        ...visibleControls.map((element) => element.getBoundingClientRect().bottom),
      );
      const compactHeight = Math.max(64, Math.ceil(contentBottom - headerRect.top + 4));

      header.style.setProperty('height', `${compactHeight}px`, 'important');
      header.style.setProperty('min-height', `${compactHeight}px`, 'important');
      header.style.setProperty('max-height', `${compactHeight}px`, 'important');

      refreshHeaderHeight();
    });
  };

  const hasSearchField = (element) =>
    Boolean(element?.querySelector("input[type='search'], input[type='text']"));

  const isVisibleCandidate = (element) => {
    if (!element || !element.isConnected || !hasSearchField(element)) return false;

    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();

    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      Number(style.opacity || 1) !== 0 &&
      rect.width > 120 &&
      rect.height > 24
    );
  };

  const candidateScore = (element) => {
    const rect = element.getBoundingClientRect();
    let score = rect.width * rect.height;

    if (isVisibleCandidate(element)) score += 1000000;
    if (element.matches(':focus-within')) score += 2000000;
    if (/mobile/i.test(element.className || '')) score += 50000;

    return score;
  };

  const getCandidates = () =>
    Array.from(document.querySelectorAll(SEARCH_SELECTOR)).filter(hasSearchField);

  const selectBestCandidate = () => {
    const candidates = getCandidates();
    if (!candidates.length) return null;

    const outsideSlot = candidates.filter((candidate) => !candidate.closest(SLOT_SELECTOR));
    const pool = outsideSlot.length ? outsideSlot : candidates;

    return pool.sort((a, b) => candidateScore(b) - candidateScore(a))[0] || null;
  };

  const clearEmptyHostMarks = () => {
    document.querySelectorAll(`.${EMPTY_HOST_CLASS}`).forEach((element) => {
      element.classList.remove(EMPTY_HOST_CLASS);
    });
  };

  const markEmptyAppHosts = (startNode) => {
    clearEmptyHostMarks();

    let node = startNode;
    let depth = 0;

    while (node && depth < 3) {
      if (
        node === document.body ||
        node === document.documentElement ||
        node.matches('main, header, footer, section, sticky-header, .section-header')
      ) {
        break;
      }

      const meaningfulChildren = Array.from(node.children).filter((child) => {
        if (['SCRIPT', 'STYLE', 'LINK', 'NOSCRIPT'].includes(child.tagName)) return false;
        if (child.classList.contains(DUPLICATE_CLASS)) return false;
        return child.getBoundingClientRect().height > 0 || child.textContent.trim().length > 0;
      });

      if (meaningfulChildren.length === 0) {
        node.classList.add(EMPTY_HOST_CLASS);
        node = node.parentElement;
        depth += 1;
      } else {
        break;
      }
    }
  };

  const restoreActiveSearch = () => {
    if (!activeSearch || !activeSearch.isConnected) return;

    if (originalParent && activeSearch.parentNode !== originalParent) {
      if (originalNextSibling && originalNextSibling.parentNode === originalParent) {
        originalParent.insertBefore(activeSearch, originalNextSibling);
      } else {
        originalParent.appendChild(activeSearch);
      }
    }

    activeSearch.classList.remove('rps-csb-container--mobile-header');
  };

  const updateDuplicateVisibility = () => {
    getCandidates().forEach((candidate) => {
      candidate.classList.toggle(
        DUPLICATE_CLASS,
        mediaQuery.matches && candidate !== activeSearch && !candidate.closest(SLOT_SELECTOR),
      );
    });
  };

  const observeActiveSearch = () => {
    if (resizeObserver) resizeObserver.disconnect();
    if (!activeSearch || !('ResizeObserver' in window)) return;

    resizeObserver = new ResizeObserver(refreshHeaderHeight);
    resizeObserver.observe(activeSearch);
  };

  const updateResponsivePlaceholder = () => {
    if (!activeSearch) return;

    const input = activeSearch.querySelector("input[type='search'], input[type='text']");
    if (!input) return;

    const viewportWidth = Math.min(
      window.innerWidth || 9999,
      document.documentElement.clientWidth || 9999,
    );
    let placeholder = SEARCH_PLACEHOLDER_FULL;

    if (viewportWidth <= 339) {
      placeholder = SEARCH_PLACEHOLDER_NARROW;
    } else if (viewportWidth <= 419) {
      placeholder = SEARCH_PLACEHOLDER_COMPACT;
    }

    if (input.placeholder !== placeholder) input.placeholder = placeholder;
    if (input.getAttribute('aria-label') !== SEARCH_PLACEHOLDER_FULL) {
      input.setAttribute('aria-label', SEARCH_PLACEHOLDER_FULL);
    }
  };

  const mountSearch = () => {
    const slot = getSlot();
    if (!slot) return false;

    if (!mediaQuery.matches) {
      restoreActiveSearch();
      activeSearch = null;
      originalParent = null;
      originalNextSibling = null;
      slot.dataset.mobileSearchReady = 'false';
      document.documentElement.classList.remove('mobile-rapid-search-mounted');
      document.querySelectorAll(`.${DUPLICATE_CLASS}`).forEach((element) => {
        element.classList.remove(DUPLICATE_CLASS);
      });
      clearEmptyHostMarks();
      normalizeMobileHeaderRow();
      refreshHeaderHeight();
      return true;
    }

    const bestCandidate = selectBestCandidate();
    if (!bestCandidate) return false;

    if (activeSearch && activeSearch !== bestCandidate) {
      restoreActiveSearch();
    }

    if (activeSearch !== bestCandidate) {
      activeSearch = bestCandidate;
      originalParent = activeSearch.parentNode;
      originalNextSibling = activeSearch.nextSibling;
    }

    if (activeSearch.parentNode !== slot) {
      slot.replaceChildren(activeSearch);
    }

    activeSearch.classList.add('rps-csb-container--mobile-header');
    updateResponsivePlaceholder();
    slot.dataset.mobileSearchReady = 'true';
    document.documentElement.classList.add('mobile-rapid-search-mounted');

    updateDuplicateVisibility();
    markEmptyAppHosts(originalParent);
    observeActiveSearch();
    normalizeMobileHeaderRow();

    return true;
  };

  const scheduleMount = () => {
    if (scheduled) return;
    scheduled = true;

    requestAnimationFrame(() => {
      scheduled = false;
      mountSearch();
    });
  };

  const nodeTouchesRapidSearch = (node) => {
    if (!(node instanceof Element)) return false;

    if (node.matches(SEARCH_SELECTOR) || node.matches(SLOT_SELECTOR)) return true;
    if (node.querySelector(SEARCH_SELECTOR) || node.querySelector(SLOT_SELECTOR)) return true;
    if (activeSearch && (node === activeSearch || node.contains(activeSearch))) return true;

    return false;
  };

  const onRelevantMutations = (mutations) => {
    if (activeSearch && !activeSearch.isConnected) {
      scheduleMount();
      return;
    }

    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (nodeTouchesRapidSearch(node)) {
          scheduleMount();
          return;
        }
      }

      for (const node of mutation.removedNodes) {
        if (nodeTouchesRapidSearch(node)) {
          scheduleMount();
          return;
        }
      }
    }
  };

  const protectHeaderWhileSearching = (event) => {
    if (!event.target.closest(SLOT_SELECTOR)) return;
    const stickyHeader = getStickyHeader();
    if (stickyHeader) stickyHeader.preventHide = event.type === 'focusin';
  };

  const onResize = () => {
    if (resizeScheduled) return;
    resizeScheduled = true;

    requestAnimationFrame(() => {
      resizeScheduled = false;
      normalizeMobileHeaderRow();
      updateResponsivePlaceholder();
    });
  };

  const initialize = () => {
    scheduleMount();

    // Observe the document only for Rapid Search / slot DOM changes.
    // Other storefront mutations (cart, sliders, chat, filters, etc.) no longer
    // trigger a full Rapid Search candidate scan and layout measurement.
    const observer = new MutationObserver(onRelevantMutations);
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });

    [100, 300, 700, 1500, 3000, 6000].forEach((delay) => {
      window.setTimeout(scheduleMount, delay);
    });

    document.addEventListener('focusin', protectHeaderWhileSearching);
    document.addEventListener('focusout', (event) => {
      if (!event.target.closest(SLOT_SELECTOR)) return;
      window.setTimeout(() => {
        const slot = getSlot();
        if (slot?.contains(document.activeElement)) return;
        const stickyHeader = getStickyHeader();
        if (stickyHeader) stickyHeader.preventHide = false;
      }, 0);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  mediaQuery.addEventListener('change', scheduleMount);
  window.addEventListener('resize', onResize, { passive: true });
  window.addEventListener('pageshow', scheduleMount);
  window.addEventListener('load', () => {
    scheduleMount();
    normalizeMobileHeaderRow();
  });
})();
